import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { TruckingVendor, TruckingVendorContract } from '~lmo/models/trucking-vendor.model';
import { LmoTruckingVendorsService } from '~lmo/services';
import { LmoTrailerTypeContractService } from '~lmo/services/lmo-trailer-type-contract.service';
import { FeatureFlagService } from '~services/feature-flag.service';
import { UserService } from '~services/user.service';
import { TrailerType } from '~v2Models/well.model';

interface PrimitiveForm {
  deadheadCostPerMile: number;
  deadheadFreeMileage: number;
  dropoffFreeTime: number;
  hourlyDetentionRate: number;
  maxDetentionPerLoad: number;
  name: string;
  pickupFreeTime: number;
}

const primitiveFormKeys: (keyof PrimitiveForm)[] = [
  'deadheadCostPerMile',
  'deadheadFreeMileage',
  'dropoffFreeTime',
  'hourlyDetentionRate',
  'maxDetentionPerLoad',
  'name',
  'pickupFreeTime',
];

interface FullForm extends PrimitiveForm {
  costsPerLoad: {
    cost: number;
    maximumMileage: number;
    minimumMileage: number;
  }[];
  id?: number;
}

const blankContract: Partial<TruckingVendorContract> = {
  deadheadCostPerMile: null,
  deadheadFreeMileage: null,
  dropoffFreeTime: null,
  hourlyDetentionRate: null,
  maxDetentionPerLoad: null,
  name: null,
  pickupFreeTime: null,
  trailerTypeId: null,
};

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LmoFracFctvContractAddeditComponent),
    },
  ],
  selector: 'sa-lmo-frac-fctv-contract-addedit',
  styleUrls: ['./lmo-frac-fctv-contract-addedit.component.scss'],
  templateUrl: './lmo-frac-fctv-contract-addedit.component.html',
})
export class LmoFracFctvContractAddeditComponent implements OnInit, ControlValueAccessor {
  public contractForm: FormGroup;
  private writeFn: (value: any) => void;
  public canEditContract = false;
  public trailerTypes$: Observable<TrailerType[]>;
  public requireTrailerType = false;
  @Input() public vendor: TruckingVendor;
  @Input() public set contract(contract: TruckingVendorContract) {
    if (contract) {
      this.setupFormGroup(contract);
    } else {
      this.setupFormGroup(blankContract);
    }
  }

  public get costsPerLoad(): FormArray {
    return this.contractForm.get('costsPerLoad') as FormArray;
  }

  public get contractFormValue(): FullForm {
    return this.contractForm.getRawValue();
  }

  constructor(
    private fb: FormBuilder,
    private truckingVendorService: LmoTruckingVendorsService,
    private userService: UserService,
    private trailerTypeContractService: LmoTrailerTypeContractService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.createCostPerLoadGroup = this.createCostPerLoadGroup.bind(this);
  }

  public ngOnInit() {
    if (!this.contractForm) {
      this.setupFormGroup(blankContract);
    }
    this.trailerTypes$ = this.trailerTypeContractService.lohiTrailerTypes$;
    this.featureFlagService
      .isFlagActive('require_frac_trailer_types')
      .pipe(take(1))
      .subscribe((isActive) => {
        this.requireTrailerType = isActive;
        const control = this.contractForm.get('trailerTypeId');
        if (control) {
          if (isActive) {
            control.setValidators([Validators.required]);
          } else {
            control.clearValidators();
          }
          control.updateValueAndValidity();
        }
      });
  }

  public writeValue(input: any): void {
    // Ignore, this is going to be a one-way trip only writing form data back so we don't have to clear on close/fail/whatever
  }

  public registerOnChange(fn: any): void {
    this.writeFn = fn;
  }

  public registerOnTouched() {}

  public saveAndUseContract() {
    if (this.contractForm.invalid) {
      return;
    }
    if (!this.canEditContract) {
      this.writeFn(this.contractFormValue);
    } else {
      if (this.contractFormValue.id) {
        this.truckingVendorService
          .updateTruckingContract$(this.contractFormValue)
          .pipe(take(1))
          .subscribe((contract) => {
            if (contract) {
              this.writeFn(contract);
            }
          });
        return;
      }
      this.truckingVendorService
        .createTruckingContract$({
          ...this.contractFormValue,
          vendorId: this.vendor.id,
        })
        .pipe(take(1))
        .subscribe((contract) => {
          if (contract) {
            this.writeFn(contract);
          }
        });
    }
  }

  public setupFormGroup(contract: Partial<TruckingVendorContract>): void {
    this.canEditContract = this.userService.hasPermission('trucking_contract_manager');
    this.contractForm = this.fb.group(contract);
    primitiveFormKeys.forEach((key) => {
      if (this.contractForm.get(key)) {
        this.contractForm.get(key).setValidators([Validators.required]);
        if (!this.canEditContract) {
          this.contractForm.get(key).disable();
        }
      }
    });
    // Apply trailer type validation and permission-based disabling
    const trailerTypeControl = this.contractForm.get('trailerTypeId');
    if (trailerTypeControl) {
      if (this.requireTrailerType) {
        trailerTypeControl.setValidators([Validators.required]);
      }
      if (!this.canEditContract) {
        trailerTypeControl.disable();
      }
      trailerTypeControl.updateValueAndValidity();
    }
    const costsPerLoadArray = (contract.costsPerLoad || []).map(this.createCostPerLoadGroup);

    // Use a form array instead of the stuff that comes in from the endpoint
    this.contractForm.removeControl('costsPerLoad');
    this.contractForm.addControl('costsPerLoad', this.fb.array(costsPerLoadArray));
    if (this.costsPerLoad.length < 1) {
      this.addCostPerLoadLine();
    }
  }

  public trackByTrailerType(_: number, type: TrailerType): number {
    return type.id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public addCostPerLoadLine() {
    this.costsPerLoad.push(this.createCostPerLoadGroup({ cost: null, maximumMileage: null, minimumMileage: null }));
  }

  public removeCostPerLoadLine(lineNumber: number) {
    if (this.costsPerLoad.length > 1) {
      this.costsPerLoad.removeAt(lineNumber);
    }
  }

  private createCostPerLoadGroup<T extends { cost: number; maximumMileage: number; minimumMileage: number }>(
    t: T,
  ): FormGroup {
    return this.fb.group({
      cost: [{ value: t.cost, disabled: !this.canEditContract }, [Validators.required]],
      maximumMileage: [
        { value: t.maximumMileage, disabled: !this.canEditContract },
        [Validators.required, Validators.min(0)],
      ],
      minimumMileage: [
        { value: t.minimumMileage, disabled: !this.canEditContract },
        [Validators.required, Validators.min(0)],
      ],
    });
  }
}
